import type { Column, Leaves, Order } from '@talos/kyoko';

/**
 * A collection of all the established custom column ids used to allow us to get some typechecking value and safety.
 * Its not perfect, but its a pragmatic approach which gives us a lot of bang for our buck.
 */
export const ORDER_CUSTOM_COLUMN_IDS = {
  FilledPercent: 'filledPercent',
  OrderAnalytics: 'orderAnalytics',
  FilledNotional: 'filledNotional',
  OpenNotional: 'openNotional',
  TotalNotional: 'totalNotional',
  FilledCounterAmount: 'filledCounterAmount',
  ProductType: 'productType',
  BaseQty: 'baseQty',
  FixingPrice: 'fixingPrice',
  HedgeOrderStatus: 'HedgeOrderStatus.HedgeControlStatus',
  UnhedgedQty: 'HedgeOrderStatus.UnhedgedQty',
  TriggerPrice: 'Parameters.TriggerPrice',
  BaseCurrency: 'baseCurrency',
  QuoteCurrency: 'quoteCurrency',
} as const;

type OrderCustomColumnID = (typeof ORDER_CUSTOM_COLUMN_IDS)[keyof typeof ORDER_CUSTOM_COLUMN_IDS];

/** All valid AG Grid colIds for the principal orders blotter. */
export type OrderBlotterColumnID = Leaves<Order> | OrderCustomColumnID;

/** Column id or partial override used in defaultColumns arrays for order blotters. */
export type OrderBlotterDefaultColumn = OrderBlotterColumnID | Partial<Column<Order>>;

/**
 * Established column ids for the principal orders blotter.
 * Maps friendly names to colIds derived from Order fields/getters and ORDER_CUSTOM_COLUMN_IDS.
 */
export const ORDER_COLUMN_IDS = {
  ...ORDER_CUSTOM_COLUMN_IDS,
  Warning: 'warning',
  SubmitTime: 'SubmitTime',
  Symbol: 'Symbol',
  ExpressedOutcome: 'ExpressedOutcome',
  Side: 'Side',
  OrderQty: 'OrderQty',
  Price: 'Price',
  PricingReference: 'PricingReference',
  Strategy: 'Strategy',
  CumQty: 'CumQty',
  FilledPx: 'filledPx',
  CumFee: 'CumFee',
  OrdStatus: 'OrdStatus',
  SelectedMarkets: 'selectedMarkets',
  TradedMarkets: 'tradedMarkets',
  SubAccount: 'SubAccount',
  SubAccounts: 'subAccounts',
  User: 'User',
  OrderID: 'OrderID',
  SelectedAndTradedMarketAccounts: 'selectedAndTradedMarketAccounts',
  AvgPx: 'AvgPx',
  RiskStatus: 'RiskStatus',
  AvgPxAllIn: 'AvgPxAllIn',
  CumAmt: 'CumAmt',
  SelectedAndTradedMarkets: 'selectedAndTradedMarkets',
  SelectedMarketAccounts: 'selectedMarketAccounts',
  TradedMarketAccounts: 'tradedMarketAccounts',
  Group: 'Group',
  EndTime: 'endTime',
  LastTradeTime: 'LastTradeTime',
  StartTime: 'startTime',
  Timestamp: 'Timestamp',
  OrdType: 'OrdType',
  ClOrdID: 'ClOrdID',
  LeavesQty: 'LeavesQty',
  RemainQty: 'remainQty',
  TimeInForce: 'TimeInForce',
  Comments: 'Comments',
  DecisionStatus: 'DecisionStatus',
  ExecID: 'ExecID',
  ExecType: 'ExecType',
  TransactTime: 'TransactTime',
  Text: 'Text',
  Parameters: 'Parameters',
  ExpectedFillPrice: 'ExpectedFillPrice',
  ExpectedFillQty: 'ExpectedFillQty',
  ParentOrderID: 'ParentOrderID',
  Revision: 'Revision',
  OrigClOrdID: 'OrigClOrdID',
  SessionID: 'SessionID',
  Allocation: 'Allocation',
  CxlRejReason: 'CxlRejReason',
  AggressorSide: 'AggressorSide',
  AllowedSlippage: 'AllowedSlippage',
  AllowedSlippageBPS: 'allowedSlippageBPS',
  PriceProtection: 'priceProtection',
  ParentRFQID: 'ParentRFQID',
  QuoteID: 'QuoteID',
  RFQID: 'RFQID',
  UnifiedLiquidity: 'unifiedLiquidity',
  ReduceOnly: 'reduceOnly',
  ReduceFirst: 'reduceFirst',
  FixingIndices: 'fixingIndices',
  FixingDetails: 'FixingDetails',
  PricingMode: 'PricingMode',
  IsArchived: 'isArchived',
  RequestSource: 'RequestSource',
  Currency: 'Currency',
  AmountCurrency: 'AmountCurrency',
  FeeCurrency: 'FeeCurrency',
  Markets: 'Markets',
  LegSummary: 'LegSummary',
  MultilegParams: 'MultilegParams',
  OrdRejReason: 'OrdRejReason',
  SettleDate: 'SettleDate',
  CumTalosFee: 'CumTalosFee',
  TalosFeeCurrency: 'TalosFeeCurrency',
  RequestUser: 'RequestUser',
  IWouldFilledQty: 'IWouldFilledQty',
  LastRequestTimestamp: 'LastRequestTimestamp',
  LastOrderTime: 'LastOrderTime',
  OrderAnnotations: 'OrderAnnotations',
  CustomerOrder: 'customerOrder',
  CustomerOrderID: 'customerOrderID',
  CustomerAccount: 'customerAccount',
  CustomerQuantity: 'customerQuantity',
  CustomerFilledQuantity: 'customerFilledQuantity',
  CustomerPrice: 'customerPrice',
  CustomerAvgPx: 'customerAvgPx',
  CustomerFilledCumAmt: 'customerFilledCumAmt',
  CustomerSymbol: 'customerSymbol',
  CustomerStrategy: 'customerStrategy',
  CustomerParameters: 'customerParameters',
  CustomerPricingParams: 'customerPricingParams',
  CustomerSummary: 'customerSummary',
  CustomerAmountCurrency: 'customerAmountCurrency',
} as const satisfies Record<string, OrderBlotterColumnID>;
